import {Injectable} from '@angular/core';
import {FieldService, FieldServiceType, UserFieldServiceAssignment} from '../models/field-service';
import {environment} from "@environments/environment";
import {RestApiManager} from "./rest-api-manager";
import {Location} from "../models/location";
import {Utils} from "../utils";
import {Paginator} from "../models/paginator";
import {AccountManagerService} from "./account-manager-service";
import {Personnel} from "../models/personnel";
import {DEBUG, DebugService} from "./debug.service";

@Injectable({
  providedIn: 'root'
})
export class FieldServiceManagerService {

  constructor(
    private accountManager: AccountManagerService,
    private debugService: DebugService,
    private restApiManager: RestApiManager,
  ) { }

  async getFieldServices(type: FieldServiceType, paginator?: Paginator<FieldService>): Promise<FieldService[]> {

    const filters = { paginator };
    if (type != null) {
      filters['params'] = { type };
    }

    return this.restApiManager.getAll<FieldService>(
      FieldService,
      `${environment.restApi.endpoint}/companies/${this.accountManager.account.company.id}/field_services`,
      this.accountManager.account.authToken,
      filters
    );
  }

  async getFieldService(fieldServiceId: string): Promise<FieldService> {
    return this.restApiManager.get<FieldService>(
      FieldService,
      `${environment.restApi.endpoint}/field_services/${fieldServiceId}`,
      this.accountManager.account.authToken
    );
  }

  // TODO al momento è possibile eseguire bind del singolo field service
  async addFieldServiceBind(fieldService: FieldService, personnel: Personnel, isManager: boolean): Promise<void> {

    // Remove previous field service
    const myFieldServices = await this.accountManager.getFieldServiceBindings(personnel.id, new Paginator<UserFieldServiceAssignment>({ ignorePagination: true }));
    if (myFieldServices.length > 0) {
      this.debugService.log(DEBUG.FIELD_SERVICE, 'Remove previous field service');
      await this.restApiManager.delete(
        `${environment.restApi.endpoint}/field_services/${myFieldServices[0].fieldService.id}/users/${personnel.id}`,
        this.accountManager.account.authToken
      );
    }

    // Remove previous field services binding if isManager is set
    /*if (isManager) {
      const myFieldServices = await this.accountManager.getFieldServiceBindings(personnel.id, new Paginator<UserFieldServiceAssignment>({ ignorePagination: true }));
      const oldFieldServiceToManage = myFieldServices.find(_fieldService => _fieldService.isManager);
      if (oldFieldServiceToManage) {

        // Remove assignment
        console.log('Remove assignment');
        await this.restApiManager.delete(
          `${environment.restApi.endpoint}/field_services/${oldFieldServiceToManage.fieldService.id}/users/${personnel.id}`,
          this.accountManager.account.authToken
        );

        // Reassign to field service without isManager flag
        console.log('Reassign to field service without isManager flag');
        this.restApiManager.post(
          `${environment.restApi.endpoint}/field_services/${oldFieldServiceToManage.fieldService.id}/users/${personnel.id}?is_manager=false`,
          { },
          this.accountManager.account.authToken
        );

      }
    }*/

    // Bind personnel with new field service
    this.debugService.log(DEBUG.FIELD_SERVICE, 'Bind personnel with new field service');
    await this.restApiManager.post(
      `${environment.restApi.endpoint}/field_services/${fieldService.id}/users/${personnel.id}?is_manager=${isManager}`,
      { },
      this.accountManager.account.authToken
    );

  }

  async removeFieldServiceBind(fieldService: FieldService, personnel: Personnel): Promise<void> {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/field_services/${fieldService.id}/users/${personnel.id}`,
      this.accountManager.account.authToken
    );
  }

  async createFieldService(fieldService: FieldService): Promise<FieldService> {

    // Create location if required
    if (!fieldService.location.isEmpty()) {
      fieldService.locationId = (await this.restApiManager.post<Location>(
        `${environment.restApi.endpoint}/locations`,
        fieldService.location,
        this.accountManager.account.authToken
      ))['id'];
    }

    // Create field service
    return this.restApiManager.post<FieldService>(
      `${environment.restApi.endpoint}/field_services`,
      { ...fieldService, companyId: this.accountManager.account.company.id },
      this.accountManager.account.authToken
    );

  }

  async updateFieldService(fieldServiceId: string, fieldService: FieldService): Promise<FieldService> {

    if (fieldService.location && !fieldService.location.isEmpty() && fieldService.location.id) {
      fieldService.locationId = fieldService.location.id;
    }

    // Not empty location fields but without id -> not exists, create location
    if (!fieldService.location.isEmpty() && Utils.isEmpty(fieldService.location.id)) {
      this.debugService.log(DEBUG.FIELD_SERVICE, 'Creating new location...');
      fieldService.locationId = (await this.restApiManager.post<Location>(
        `${environment.restApi.endpoint}/locations`,
        fieldService.location,
        this.accountManager.account.authToken
      ))['id'];
    }

    // Not empty location fields but with id -> update location
    if (!fieldService.location.isEmpty() && !Utils.isEmpty(fieldService.location.id)) {
      this.debugService.log(DEBUG.FIELD_SERVICE, 'Updating location...');
      await this.restApiManager.put<Location>(
        `${environment.restApi.endpoint}/locations/${fieldService.location.id}`,
        fieldService.location,
        this.accountManager.account.authToken
      );
    }

    // Update field service
    const updatedFieldService = await this.restApiManager.put<FieldService>(
      `${environment.restApi.endpoint}/field_services/${fieldServiceId}`,
      { ...fieldService, companyId: this.accountManager.account.company.id },
      this.accountManager.account.authToken
    );

    // Empty location fields but with id -> exists, remove location
    if (fieldService.location.isEmpty() && !Utils.isEmpty(fieldService.location.id)) {
      this.debugService.log(DEBUG.FIELD_SERVICE, 'Removing location...');
      await this.restApiManager.delete(
        `${environment.restApi.endpoint}/locations/${fieldService.location.id}`,
        this.accountManager.account.authToken
      );
      fieldService.locationId = null;
    }

    return updatedFieldService;

  }

  async removeFieldService(fieldService: FieldService): Promise<void> {

    // Remove field service
    await this.restApiManager.delete(
      `${environment.restApi.endpoint}/field_services/${fieldService.id}`,
      this.accountManager.account.authToken
    );

    // Remove location
    if (fieldService.location && !Utils.isEmpty(fieldService.location.id)) {
      this.debugService.log(DEBUG.FIELD_SERVICE, 'Removing location...');
      await this.restApiManager.delete(
        `${environment.restApi.endpoint}/locations/${fieldService.location.id}`,
        this.accountManager.account.authToken
      );
    }

  }

  getUserFromFieldService(fieldService: FieldService, paginator?: Paginator<Personnel>): Promise<Personnel[]> {
    return this.restApiManager.getAll<Personnel>(
      Personnel,
      `${environment.restApi.endpoint}/field_services/${fieldService.id}/users`,
      this.accountManager.account.authToken,
      { paginator }
    );
  }

}
